import { bugsnag } from 'shared/utils/services/bugsnag';
import { convertToDollars } from 'shared/utils/formatCurrency';
import * as Yup from 'yup';
import { t } from 'localization/utils/t';
import { getMParticle } from './getMParticle';

const TP = 'tracking.utils.eCommerceEvents';

interface ICreateProductData {
  name: string;
  sku: string;
  priceCents: number;
  brandName?: string;
  condition?: string;
}

interface IOrderForLogPurchase {
  number?: number;
  priceCents?: number;
}

/**
 * mParticle ecommerce events, these event automatically map to events needed for 3rd
 * party data tracking integrations (Facebook and Google Analytics)
 * https://docs.mparticle.com/developers/sdk/web/commerce-tracking/
 */

// calls createProduct event in mParticle (needed for many of the ecom events)
export const createProduct = (data: ICreateProductData, mparticle: any) => {
  const { name, sku, priceCents, brandName, condition = 'new' } = data;
  const quantity = 1;

  const createProductSchema = Yup.object({
    priceCents: Yup.number()
      .min(1)
      .required(
        t(
          `${TP}.priceCentsRequired`,
          'Price cents ({priceCents}) not supplied as number to mParticle create product, pt: {name}, price is required',
          {
            priceCents,
            name,
          },
        ),
      ),
    sku: Yup.string()
      .min(1)
      .required(
        t(
          `${TP}.skuRequired`,
          'Sku not supplied to mParticle create product, pt: {name}, sku is required',
          {
            name,
          },
        ),
      ),
    name: Yup.string()
      .min(1)
      .required(
        t(
          `${TP}.nameRequired`,
          'Name not supplied to mParticle create product, sku: {sku}, name is required',
          {
            sku,
          },
        ),
      ),
  });

  try {
    createProductSchema.validateSync(data);
  } catch {
    return;
  }

  const price = convertToDollars(priceCents);
  try {
    // name, sku, price required for mParticle createProduct
    return mparticle.eCommerce.createProduct(name, sku, price, quantity, brandName, condition);
  } catch (error) {
    bugsnag(
      `There was an error creating a product for mParticle logging, ${JSON.stringify(error)}`,
      { context: 'eCommerceEvents' },
    );
  }
};

export function logViewDetail(product: ICreateProductData) {
  const mParticle = getMParticle();

  if (!mParticle) {
    return;
  }

  window.mParticle.ready(() => {
    const mParticleProduct = createProduct(product, window.mParticle);

    if (mParticleProduct) {
      try {
        window.mParticle.eCommerce.logProductAction(
          window.mParticle.ProductActionType.ViewDetail,
          mParticleProduct,
          {
            content_type: 'product_group',
          },
        );
      } catch (error) {
        bugsnag(error, {
          context: 'eCommerceEvents',
        });
      }
    }
  });
}

export function logAddToCart(product: ICreateProductData) {
  const mParticle = getMParticle();

  if (!mParticle) {
    return;
  }

  const mParticleProduct = createProduct(product, mParticle);
  if (mParticleProduct) {
    try {
      mParticle.eCommerce.logProductAction(
        mParticle.ProductActionType.AddToCart,
        mParticleProduct,
        { content_type: 'product_group' },
      );
    } catch (error) {
      bugsnag(error, {
        context: 'eCommerceEvents',
      });
    }
  }
}

export function logCartCheckout(product: ICreateProductData) {
  const mParticle = getMParticle();

  if (!mParticle) {
    return;
  }

  const mParticleProduct = createProduct(product, mParticle);
  if (mParticleProduct) {
    try {
      mParticle.eCommerce.logProductAction(mParticle.ProductActionType.Checkout, mParticleProduct, {
        content_type: 'product_group',
      });
    } catch (error) {
      bugsnag(error, {
        context: 'eCommerceEvents',
      });
    }
  }
}

export function logEcomPurchase(product: ICreateProductData, order: IOrderForLogPurchase) {
  const mParticle = getMParticle();

  if (!mParticle) {
    return;
  }
  const orderSchema = Yup.object({
    number: Yup.number()
      .min(1)
      .required(
        t(
          `${TP}.numberRequired`,
          'Order number not supplied to log purchase, unable to log for product: {name}',
          { name: product.name },
        ),
      ),
    priceCents: Yup.number()
      .min(1)
      .required(
        t(
          `${TP}.priceCentsRequiredTwo`,
          'Price cents({priceCents}) not supplied to mParticle log purchase, order: {number}',
          {
            priceCents: order.priceCents,
            number: order.number,
          },
        ),
      ),
  });

  try {
    orderSchema.validateSync(order);
  } catch (error) {
    bugsnag(error.errors[0], { context: 'eCommerceEvents' });
    return;
  }

  const revenue = convertToDollars(order.priceCents);

  try {
    const transactionAttributes = mParticle.eCommerce.createTransactionAttributes(
      order?.number,
      null,
      null,
      revenue,
    );
    const mParticleProduct = createProduct(product, mParticle);
    if (mParticleProduct) {
      mParticle.eCommerce.logProductAction(
        mParticle.ProductActionType.Purchase,
        mParticleProduct,
        {
          content_type: 'product_group',
          transaction_id: order?.number,
        },
        null,
        transactionAttributes,
      );
    }
  } catch (error) {
    bugsnag(error, {
      context: 'eCommerceEvents',
    });
  }
}
